/**
 * @fileoverview Tracking Metrics Collection
 * @module frontend/services/tracking/tracking-metrics.ts
 *
 * Collects and exposes metrics for the tracking service including:
 * - Requests sent/failed
 * - Retry attempts
 * - Circuit breaker state
 * - Queue size
 * - Performance metrics
 */

/**
 * Tracking metrics snapshot
 */
export interface TrackingMetricsSnapshot {
  /** Total events tracked */
  totalEventsTracked: number
  /** Total events sent successfully */
  totalEventsSent: number
  /** Total events failed */
  totalEventsFailed: number
  /** Total requests sent */
  totalRequestsSent: number
  /** Total requests failed */
  totalRequestsFailed: number
  /** Total retry attempts */
  totalRetryAttempts: number
  /** Current queue size */
  currentQueueSize: number
  /** Circuit breaker state */
  circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN'
  /** Circuit breaker failure count */
  circuitBreakerFailures: number
  /** Timestamp of metrics snapshot */
  timestamp: number
  /** Uptime in milliseconds */
  uptime: number
  /** Average events per request */
  avgEventsPerRequest: number
  /** Success rate (0-100) */
  successRate: number
}

/**
 * Tracking Metrics Collector
 *
 * Collects metrics for the tracking service to monitor performance
 * and health. Metrics can be accessed via console or monitoring endpoint.
 *
 * @example
 * ```typescript
 * const metrics = new TrackingMetrics()
 * metrics.recordEventTracked()
 * metrics.recordRequestSent(10)
 * metrics.recordRequestFailed()
 * const snapshot = metrics.getSnapshot()
 * console.log(snapshot)
 * ```
 */
export class TrackingMetrics {
  private totalEventsTracked = 0
  private totalEventsSent = 0
  private totalEventsFailed = 0
  private totalRequestsSent = 0
  private totalRequestsFailed = 0
  private totalRetryAttempts = 0
  private currentQueueSize = 0
  private circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED'
  private circuitBreakerFailures = 0
  private startTime = Date.now()

  constructor() {
    // Expose metrics to window for debugging
    if (typeof window !== 'undefined') {
      (window as unknown as { __trackingMetrics?: TrackingMetrics }).__trackingMetrics = this
    }
  }

  /**
   * Record an event being tracked
   *
   * @param count - Number of events (default: 1)
   */
  recordEventTracked(count: number = 1): void {
    this.totalEventsTracked += count
  }

  /**
   * Record events sent successfully
   *
   * @param count - Number of events sent
   */
  recordEventsSent(count: number): void {
    this.totalEventsSent += count
  }

  /**
   * Record events failed
   *
   * @param count - Number of events failed (default: 1)
   */
  recordEventsFailed(count: number = 1): void {
    this.totalEventsFailed += count
  }

  /**
   * Record a request sent
   *
   * @param eventCount - Number of events in the request
   */
  recordRequestSent(eventCount: number): void {
    this.totalRequestsSent += 1
    this.recordEventsSent(eventCount)
  }

  /**
   * Record a request failed
   *
   * @param eventCount - Number of events in the failed request
   */
  recordRequestFailed(eventCount: number): void {
    this.totalRequestsFailed += 1
    this.recordEventsFailed(eventCount)
  }

  /**
   * Record a retry attempt
   *
   * @param count - Number of retry attempts (default: 1)
   */
  recordRetryAttempt(count: number = 1): void {
    this.totalRetryAttempts += count
  }

  /**
   * Update current queue size
   *
   * @param size - Current queue size
   */
  updateQueueSize(size: number): void {
    this.currentQueueSize = size
  }

  /**
   * Update circuit breaker state
   *
   * @param state - Circuit breaker state
   * @param failures - Number of failures (for OPEN state)
   */
  updateCircuitBreakerState(state: 'CLOSED' | 'OPEN' | 'HALF_OPEN', failures?: number): void {
    this.circuitBreakerState = state
    if (failures !== undefined) {
      this.circuitBreakerFailures = failures
    }
  }

  /**
   * Get current metrics snapshot
   *
   * @returns Metrics snapshot
   */
  getSnapshot(): TrackingMetricsSnapshot {
    const totalRequests = this.totalRequestsSent + this.totalRequestsFailed
    const avgEventsPerRequest = totalRequests > 0 ? this.totalEventsSent / totalRequests : 0
    const successRate =
      this.totalEventsSent + this.totalEventsFailed > 0
        ? (this.totalEventsSent / (this.totalEventsSent + this.totalEventsFailed)) * 100
        : 0

    return {
      totalEventsTracked: this.totalEventsTracked,
      totalEventsSent: this.totalEventsSent,
      totalEventsFailed: this.totalEventsFailed,
      totalRequestsSent: this.totalRequestsSent,
      totalRequestsFailed: this.totalRequestsFailed,
      totalRetryAttempts: this.totalRetryAttempts,
      currentQueueSize: this.currentQueueSize,
      circuitBreakerState: this.circuitBreakerState,
      circuitBreakerFailures: this.circuitBreakerFailures,
      timestamp: Date.now(),
      uptime: Date.now() - this.startTime,
      avgEventsPerRequest: Math.round(avgEventsPerRequest * 100) / 100,
      successRate: Math.round(successRate * 100) / 100,
    }
  }

  /**
   * Get metrics as formatted string for console logging
   *
   * @returns Formatted metrics string
   */
  getFormattedMetrics(): string {
    const snapshot = this.getSnapshot()
    const uptime = Math.round(snapshot.uptime / 1000)

    return `
📊 Tracking Metrics (uptime: ${uptime}s)
├─ Events: ${snapshot.totalEventsTracked} tracked, ${snapshot.totalEventsSent} sent, ${snapshot.totalEventsFailed} failed
├─ Requests: ${snapshot.totalRequestsSent} sent, ${snapshot.totalRequestsFailed} failed
├─ Retries: ${snapshot.totalRetryAttempts} attempts
├─ Queue: ${snapshot.currentQueueSize} events
├─ Circuit Breaker: ${snapshot.circuitBreakerState} (${snapshot.circuitBreakerFailures} failures)
├─ Avg Events/Request: ${snapshot.avgEventsPerRequest}
└─ Success Rate: ${snapshot.successRate}%
    `.trim()
  }

  /**
   * Log metrics to console
   */
  logMetrics(): void {
    console.log(this.getFormattedMetrics())
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.totalEventsTracked = 0
    this.totalEventsSent = 0
    this.totalEventsFailed = 0
    this.totalRequestsSent = 0
    this.totalRequestsFailed = 0
    this.totalRetryAttempts = 0
    this.currentQueueSize = 0
    this.circuitBreakerState = 'CLOSED'
    this.circuitBreakerFailures = 0
    this.startTime = Date.now()
  }

  /**
   * Get metrics as JSON
   *
   * @returns JSON representation of metrics
   */
  toJSON(): TrackingMetricsSnapshot {
    return this.getSnapshot()
  }
}
